/**
 * Playback coalescer
 *
 * Pure rate-limiting primitive used by the server-synced played-chapter
 * history feature. Per `key`, emissions are capped at one per `windowMs`:
 * the first `schedule(key, value, emit)` fires immediately; subsequent
 * `schedule` calls within the window queue the latest value and emit it
 * when the window elapses. `flush(key)` and `flushAll()` emit the latest
 * pending value(s) immediately and re-arm the window.
 *
 * Contract is purely synchronous against `Date.now()` and `setTimeout` —
 * no React, no DOM. See specs/006-sync-played-chapters/data-model.md
 * (FR-014, FR-014a, SC-007).
 */

const DEFAULT_WINDOW_MS = 10000;

export function createPlaybackCoalescer({ windowMs = DEFAULT_WINDOW_MS } = {}) {
  if (!Number.isFinite(windowMs) || windowMs <= 0) {
    throw new Error('createPlaybackCoalescer: windowMs must be a positive number');
  }

  const slots = new Map();

  function getSlot(key) {
    let slot = slots.get(key);
    if (!slot) {
      slot = {
        lastEmitAt: null,
        pendingValue: undefined,
        pendingEmit: null,
        hasPending: false,
        timer: null,
      };
      slots.set(key, slot);
    }
    return slot;
  }

  function emitNow(key) {
    const slot = slots.get(key);
    if (!slot || !slot.hasPending) return;
    const value = slot.pendingValue;
    const emit = slot.pendingEmit;
    slot.lastEmitAt = Date.now();
    slot.pendingValue = undefined;
    slot.pendingEmit = null;
    slot.hasPending = false;
    if (slot.timer != null) {
      clearTimeout(slot.timer);
      slot.timer = null;
    }
    try {
      emit(value);
    } catch (e) {
      console.warn('PlaybackCoalescer: emit threw', e);
    }
  }

  function schedule(key, value, emit) {
    if (typeof emit !== 'function') return;
    const slot = getSlot(key);
    slot.pendingValue = value;
    slot.pendingEmit = emit;
    slot.hasPending = true;

    if (slot.lastEmitAt == null) {
      emitNow(key);
      return;
    }

    const elapsed = Date.now() - slot.lastEmitAt;
    if (elapsed >= windowMs) {
      emitNow(key);
      return;
    }

    if (slot.timer == null) {
      const remaining = windowMs - elapsed;
      slot.timer = setTimeout(() => {
        slot.timer = null;
        emitNow(key);
      }, remaining);
    }
  }

  function flush(key) {
    const slot = slots.get(key);
    if (!slot || !slot.hasPending) return;
    emitNow(key);
  }

  function flushAll() {
    for (const key of Array.from(slots.keys())) {
      flush(key);
    }
  }

  return { schedule, flush, flushAll };
}
